import { useState, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import { v4 as uuidv4 } from 'uuid'
import { PDFDocument } from 'pdf-lib'
const API_URL = process.env.REACT_APP_API_URL;

export default function ClientDocumentsPage() {
  const [checklistItems, setChecklistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingItemId, setUploadingItemId] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState({});
  const [resetKey, setResetKey] = useState(0);
  const [uploadStatus, setUploadStatus] = useState({});
  const [successItemId, setSuccessItemId] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);

  const token = localStorage.getItem('clientToken');
  const caseId = localStorage.getItem('clientCaseId');

  useEffect(() => {
    if (!token || !caseId) return;
    fetch(`${API_URL}/case/${caseId}/checklist/`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.json())
      .then(data => setChecklistItems(data))
      .catch(err => console.error('Failed to load checklist', err))
      .finally(() => setLoading(false));
  }, [token, caseId]);

  useEffect(() => {
    if (showBookingModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [showBookingModal]);



  const totalItems = checklistItems.length;

  const points = checklistItems.reduce((sum, item) => {
    const statuses = item.uploads.map(u => u.status);
    if (statuses.includes("accepted")) return sum + 1;
    if (statuses.includes("awaiting_review")) return sum + 0.5;
    return sum; // Rejected or no uploads
  }, 0);
  
  const progress = totalItems ? Math.round((points / totalItems) * 100) : 0;
  const acceptedCount = checklistItems.filter(item =>
    item.uploads.some(u => u.status === 'accepted')
  ).length;
  const awaitingCount = checklistItems.filter(item =>
    !item.uploads.some(u => u.status === 'accepted') &&
    item.uploads.some(u => u.status === 'awaiting_review')
  ).length;
  
  const neededCount = checklistItems.length - acceptedCount - awaitingCount;

  const getStatusTint = (item) => {
    const statuses = item.uploads.map(u => u.status);
    if (statuses.includes('accepted')) return 'bg-green-50';
    if (statuses.includes('awaiting_review')) return 'bg-yellow-50';
    if (statuses.includes('rejected')) return 'bg-red-50';
    return 'bg-white';
  };
const getItemDisplayStatus = (item) => {
  const statuses = (item?.uploads || []).map(u => u.status);
  if (statuses.includes('accepted')) return 'accepted';
  if (statuses.includes('awaiting_review')) return 'awaiting_review';
  // no uploads OR only rejected uploads => still needed
  return 'needed';
};

const getLatestTrusteePacket = (item) => {
  const td = item?.trustee_docs;
  if (!td) return null;

  // some serializers return single object, some return array
  const arr = Array.isArray(td) ? td : [td];

  // newest by created_at
  return arr
    .filter(Boolean)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0] || null;
};

  const sortedChecklistItems = [...checklistItems].sort((a, b) => {
  const order = { needed: 0, awaiting_review: 1, accepted: 2 };
  const sa = getItemDisplayStatus(a);
  const sb = getItemDisplayStatus(b);
  return (order[sa] ?? 99) - (order[sb] ?? 99);
});
const getStatusBadge = (status) => {
  switch (status) {
    case 'accepted':
      return { label: '✅ Completed', cls: 'bg-green-600 text-white' };
    case 'awaiting_review':
      return { label: '🕐 Awaiting Review', cls: 'bg-yellow-500 text-black' };
    default:
      return { label: '📄 Still Needed', cls: 'bg-red-600 text-white' };
  }
};
  async function enhanceImage(file) {
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext("2d");
  
    // Draw image
    ctx.drawImage(bitmap, 0, 0);
  
    // Get image data
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
  
    // Convert to grayscale and boost contrast
    for (let i = 0; i < data.length; i += 4) {
      // Luminance formula
      const grayscale = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
  
      // Boost contrast (simple linear contrast adjustment)
      const contrast = 1.5; // You can adjust this value if needed
      const contrasted = (grayscale - 128) * contrast + 128;
  
      // Clamp to 0–255
      const final = Math.max(0, Math.min(255, contrasted));
  
      data[i] = data[i + 1] = data[i + 2] = final;
    }
  
    ctx.putImageData(imageData, 0, 0);
  
    // Export as compressed JPEG
    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          const enhancedFile = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), {
            type: "image/jpeg",
          });
          resolve(enhancedFile);
        },
        "image/jpeg",
        1 // ← low quality to reduce size
      );
    });
  }
  // Merge multiple PDF files into a single File using PDF-lib
  async function mergePDFs(files, baseName) {
    const mergedPdf = await PDFDocument.create();
    for (const file of files) {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
      const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
      copiedPages.forEach(page => mergedPdf.addPage(page));
    }
    const mergedPdfBytes = await mergedPdf.save();
    return new File([mergedPdfBytes], `${baseName}.pdf`, { type: 'application/pdf' });
  }
  
  async function createMultiPagePDF(images, checkListName, checkListId) {
    const pageWidth = 612;
    const pageHeight = 792;
    const maxImageSize = 1800; // px, max width or height (higher to keep text screenshots sharper)
    const FIVE_MB = 10 * 1024 * 1024; // allow up to ~10MB before recompressing
  
    // Helper: build PDF with a given JPEG quality
    async function buildPdfWithQuality(quality) {
      const pdfDoc = new jsPDF({ unit: "pt", format: "letter" });
      for (let i = 0; i < images.length; i++) {
        const imgBitmap = await createImageBitmap(images[i]);
  
        // 1) Resize image (max 900px)
        const scale = Math.min(1, maxImageSize / Math.max(imgBitmap.width, imgBitmap.height));
        const resizedW = Math.floor(imgBitmap.width * scale);
        const resizedH = Math.floor(imgBitmap.height * scale);
  
        const canvas = document.createElement("canvas");
        canvas.width = resizedW;
        canvas.height = resizedH;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(imgBitmap, 0, 0, resizedW, resizedH);
  
        // 2) Convert to JPEG at requested quality
        const dataUrl = canvas.toDataURL("image/jpeg", quality);
  
        // 3) Center & scale on letter page
        const pdfScale = Math.min(pageWidth / resizedW, pageHeight / resizedH);
        const pdfW = resizedW * pdfScale;
        const pdfH = resizedH * pdfScale;
        const x = (pageWidth - pdfW) / 2;
        const y = (pageHeight - pdfH) / 2;
  
        if (i > 0) pdfDoc.addPage();
        pdfDoc.addImage(dataUrl, "JPEG", x, y, pdfW, pdfH);
      }
      return pdfDoc.output("blob");
    }
  
    // First pass: build at high JPEG quality, then inspect size
    let blob = await buildPdfWithQuality(0.95);

    // If the first pass is over the size limit, rebuild at a slightly lower quality
    if (blob.size > FIVE_MB) {
      console.warn(`PDF is ${(blob.size / (1024 * 1024)).toFixed(2)} MB – recompressing at 0.8`);
      blob = await buildPdfWithQuality(0.8);
    }
  
    // Generate a “safe” filename
    const safeName = checkListName
      .split("?")[0]
      .replace(/^https?:\/\//, "")
      .replace(/[|\s\/]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 10);
  
    const uniqueId =
      (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
      uuidv4();
  
    const fileName = `${safeName}-${uniqueId}.pdf`;
    return new File([blob], fileName, { type: "application/pdf" });
  }

  const handleSelectFiles = async (event, itemId) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;
    setSelectedFiles(prev => ({ ...prev, [itemId]: [...(prev[itemId] || []), ...files] }));
  };

  const handleRemoveFile = (itemId, indexToRemove) => {
    setSelectedFiles(prev => ({ ...prev, [itemId]: prev[itemId].filter((_, i) => i !== indexToRemove) }));
  };

  const handleSubmitFiles = async (itemId, itemName) => {
    const files = selectedFiles[itemId];
    if (!files || !files.length) return;
    setUploadingItemId(itemId);
    setUploadStatus(prev => ({ ...prev, [itemId]: 'uploading' }));

    const skipEnhance = /photo id|social security/i.test(itemName);
    const images = files.filter(f => f.type.startsWith('image/'));
    const pdfs = files.filter(f => f.type === 'application/pdf');

    try {
      if (images.length > 0) {
        const processedImages = skipEnhance ? images : await Promise.all(images.map(enhanceImage));
        const combinedPDF = await createMultiPagePDF(processedImages, itemName,itemId);
        const formData = new FormData();
        formData.append('file', combinedPDF);
        await fetch(`${API_URL}/checklist-item/${itemId}/upload/`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
      }
      function makeSafeName(base, checklistId) {
        return base
          .split('?')[0]
          .replace(/^https?:\/\//, '')
          .replace(/[|\s/]+/g, '-')
          .replace(/^-+|-+$/g, '').slice(0, 10);
      }
      const base = makeSafeName(itemName, itemId);
      if (pdfs.length > 0) {
        const encryptedFiles = [];
        const plainFiles = [];
        for (const file of pdfs) {
          try {
            // Detect encryption
            await PDFDocument.load(
              await file.arrayBuffer(),
              { ignoreEncryption: false }
            );
            plainFiles.push(file);
          } catch (err) {
            encryptedFiles.push(file);
          }
        }
        // Merge & upload plain PDFs
        if (plainFiles.length > 0) {
          const mergedFile = await mergePDFs(
            plainFiles,
            `${base}${window.crypto?.randomUUID?.() || uuidv4()}`
          );
          const formData = new FormData();
          formData.append('file', mergedFile);
          await fetch(`${API_URL}/checklist-item/${itemId}/upload/`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
          });
        }
        // Upload encrypted PDFs as‐is
          for (const file of encryptedFiles) {
            // build a unique filename just like you do for merged PDFs
            const uniqueName = `${base}-${window.crypto?.randomUUID?.() || uuidv4()}.pdf`;
            // wrap the original file in a new File with your unique name
            const renamedFile = new File([file], uniqueName, { type: file.type });
            const formData = new FormData();
            formData.append('file', renamedFile);
            await fetch(`${API_URL}/checklist-item/${itemId}/upload/`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}` },
              body: formData,
            });
          }
    }
      const refreshed = await fetch(`${API_URL}/case/${caseId}/checklist/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await refreshed.json();
      setChecklistItems(data);
      setSelectedFiles(prev => ({ ...prev, [itemId]: [] }));
      setResetKey(prev => prev + 1);

    } catch (err) {
      console.error('Upload failed', err);
    } finally {
      setUploadingItemId(null);
      setSuccessItemId(itemId);
        setTimeout(() => setSuccessItemId(null), 1500);
    }
  };
const handleDownloadTrusteeDoc = async (packetId, kind) => {
  try {
    const res = await fetch(`${API_URL}/case/trustee-doc-packets/${packetId}/presign/?kind=${kind}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!res.ok) {
      const err = await res.text();
      console.error("Presign failed:", res.status, err);
      alert("Could not generate download link.");
      return;
    }

    const data = await res.json();
    if (!data?.signed_url) {
      alert("No download link returned.");
      return;
    }

    window.open(data.signed_url, "_blank", "noopener,noreferrer");
  } catch (e) {
    console.error(e);
    alert("Download failed.");
  }
};
  return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-center mb-6 text-indigo-700">📁 Document Center</h1>

        <div className="bg-white p-4 rounded shadow mb-6">
        <h2 className="text-lg font-semibold mb-2">Progress</h2>
        <div className="w-full bg-gray-200 h-4 rounded">
            <div
            className="h-4 bg-green-500 rounded"
            style={{ width: `${progress}%` }}
            ></div>
        </div>

        {progress === 100 ? (
            <div className="mt-4 text-center">
                <p className="text-green-700 font-medium mb-2">
                All documents accepted! You can now schedule your case filing appointment.
                </p>
                <button
                onClick={() => setShowBookingModal(true)}
                className="px-6 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition"
                >
                Schedule Appointment
                </button>
            </div>
            ) : (<div>
            <p className="text-sm mt-1 text-gray-600">{progress}% complete</p>
            <p className="text-sm mt-1 text-gray-600">✅ {acceptedCount} item(s) accepted</p>
            <p className="text-sm mt-1 text-gray-600">🕐 {awaitingCount} awaiting approval</p>
            <p className="text-sm mt-1 text-gray-600">📄 {neededCount} still needed</p></div>
            )}
        </div>

        {loading ? (
          <p>Loading checklist...</p>
        ) : (
          <div className="space-y-6">
            {sortedChecklistItems.map(item => (
                <div key={item.id} className="relative">
                  {(uploadingItemId === item.id || successItemId === item.id) && (
                    <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center rounded z-10">
                    {uploadingItemId === item.id ? (
                        <div className="h-6 w-6 border-4 border-blue-500 border-t-transparent animate-spin rounded-full"></div>
                    ) : (
                        <svg
                        className="h-8 w-8 text-green-500 animate-scale-in"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        viewBox="0 0 24 24"
                        >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                    )}
                    </div>
                )}
              <div className={`border rounded p-4 pt-12 shadow ${getStatusTint(item)} relative`}>
                  {(() => {
                    const st = getItemDisplayStatus(item);
                    const badge = getStatusBadge(st);
                    return (
                      <div className={`absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-extrabold tracking-wide shadow ${badge.cls}`}>
                        {badge.label}
                      </div>
                    );
                  })()}
                <h3 className="text-lg font-semibold text-gray-900">{item.name}</h3>
                <p className="text-sm text-gray-600 mb-2">{item.description}</p>
              {(() => {
                const packet = getLatestTrusteePacket(item);
                if (!packet) return null;

                const hasPdf = !!packet.generated_pdf;
                const hasDocx = !!packet.generated_docx;
                if (!hasPdf && !hasDocx) return null;

                return (
                  <div className="mt-4 rounded-lg border border-indigo-200 bg-indigo-50 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-bold text-indigo-900">📝 Trustee document to complete</div>
                        <div className="mt-1 text-xs text-indigo-800">
                          Download the form below, fill it out, then upload it back to <span className="font-semibold">this same checklist item</span>.
                        </div>
                      </div>
                      <div className="shrink-0 rounded-full bg-white px-3 py-1 text-xs font-semibold text-indigo-800 ring-1 ring-indigo-200">
                        Required
                      </div>
                    </div>

                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {hasPdf && (
                        <button
                          type="button"
                          onClick={() => handleDownloadTrusteeDoc(packet.id, "pdf")}
                          className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-indigo-700 px-4 py-2 text-sm font-bold text-white shadow hover:bg-indigo-800 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                        >
                          ⬇️ Download PDF (recommended)
                        </button>
                      )}

                      {hasDocx && (
                        <button
                          type="button"
                          onClick={() => handleDownloadTrusteeDoc(packet.id, "docx")}
                          className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-bold text-indigo-800 ring-1 ring-indigo-200 shadow-sm hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                        >
                          ⬇️ Download Word (.docx)
                        </button>
                      )}
                    </div>

                    <div className="mt-2 text-xs text-indigo-800">
                      Tip: After you upload, we’ll review it and let you know if anything needs fixing.
                    </div>
                    
                  </div>
                  
                );
              })()}
              
                <label className="mt-4 cursor-pointer inline-block bg-green-200 text-black-700 px-4 py-2 rounded-full text-sm font-semibold hover:bg-blue-100">
                  Choose Files / Take Photos
                  <input
                    key={resetKey}
                    type="file"
                    accept="image/*,application/pdf"
                    multiple
                    onChange={(e) => handleSelectFiles(e, item.id)}
                    className="hidden"
                  />
                </label>
                <p className="text-xs text-gray-500 mt-1">You can upload multiple images or PDFs at once.</p>
                {selectedFiles[item.id]?.length > 0 && (
                  <ul className="text-sm text-gray-700 mt-2 space-y-1">
                    {selectedFiles[item.id].map((file, idx) => (
                      <li key={idx} className="flex justify-between items-center">
                        <span>{file.name}</span>
                        <button onClick={() => handleRemoveFile(item.id, idx)} className="text-red-600 hover:underline text-xs">
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <button
                onClick={() => handleSubmitFiles(item.id, item.name)}
                disabled={!selectedFiles[item.id] || uploadingItemId === item.id}
                className="mt-2 sm:mt-0 sm:ml-4 bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 disabled:bg-gray-300 w-full sm:w-auto"
                >
                {uploadingItemId === item.id ? 'Uploading...' : 'Submit Documents'}
                </button>

                

                <div className="mt-3">
                  {item.uploads.length > 0 ? (
                    <ul className="space-y-1 text-sm">
                      {item.uploads.map(upload => (
                        <li key={upload.id}>
                          <a
                            href={upload.signed_url}
                            className="text-blue-600 hover:underline"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {upload.filename}
                          </a>{' '} - <span className="text-gray-500">{upload.status}</span>
                          {upload.review_notes && <p className="text-xs italic text-yellow-600">Note: {upload.review_notes}</p>}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No document uploaded yet.</p>
                  )}
                </div>
              </div></div>
            ))}
          </div>
        )}
        {showBookingModal && (
            <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center px-2">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl h-[95vh] max-h-[80vh] relative overflow-hidden">
                <button
                onClick={() => setShowBookingModal(false)}
                className="absolute top-3 right-4 text-gray-600 hover:text-red-600 text-lg z-10"
                >
                ✕
                </button>
                <iframe
                src="https://outlook.office.com/owa/calendar/CisowskiLawCaseFilingAppointment@NETORGFT5747048.onmicrosoft.com/bookings/"
                width="100%"
                height="100%"
                className="rounded-b-lg"
                title="Schedule Appointment"
                style={{ border: 0 }}
                />
            </div>
            </div>
            )}
      </div>
  );
}
